// The voice session, with no React in it: one microphone, one transcription session, one
// speaker, and the rules that join them to whichever conversation is on screen. The hook in
// `useVoice.ts` only mirrors `state` into React.
//
// No audio passes through Mor's server. The browser talks to both vendors with short-lived
// tokens from `client.voiceToken`.

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { Message, Person, VoiceTokens } from '../api/contract.js';
import { FACILITATOR_NAME, FILE_MARKER } from '../components/MessageRow.js';
import { startCapture, type Capture } from './capture.js';
import { voiceLog, wordCount } from './log.js';
import { openTranscription, SPEECH_MODEL, type Transcription } from './stt.js';
import { createSpeaker, type Speaker } from './tts.js';

export type VoicePhase = 'starting' | 'listening' | 'thinking' | 'speaking';

export type VoiceTarget = {
  threadId: string;
  messages: readonly Message[];
  /** The roster the conversation renders names from. */
  people: readonly Person[];
  /** Whether spoken messages start with the sender's name. */
  named: boolean;
  post: (body: string) => Promise<unknown>;
  facilitatorBusy: boolean;
  loaded: boolean;
};

export type VoiceState = {
  on: boolean;
  phase: VoicePhase;
  partial: string;
  note: string | null;
  /** Changes whenever speech is kept for a conversation's text box. */
  unsentVersion: number;
};

// A commit happens this long after the last final with no new partial: it merges a turn the
// vendor split in two.
const SETTLE_MS = 300;
// A partial unchanged this long with no final is committed anyway. Longer than the vendor's
// own two-second endpoint, so it fires only when endpointing failed.
const STALL_MS = 4000;
const THINKING_TIMEOUT_MS = 60_000;
const BARGE_RMS = 0.06;
const BARGE_MS = 250;
// How often the meter may repaint.
const LEVEL_EVERY_MS = 80;
// Frames (about 100 ms each) kept while a new transcription session is being opened.
const MAX_HELD_FRAMES = 20;
// After a switch, the previous conversation's transcription session gets this long to turn
// audio it already has into text before it is closed.
const DRAIN_MS = 1000;

const NOTE_MIC = "Voice needs the microphone, and it wasn't allowed.";
const NOTE_UNAVAILABLE = "Voice isn't set up here yet.";
const NOTE_DROPPED = 'Voice stopped: the connection dropped.';
const NOTE_NOT_SENT = "That wasn't sent.";

export function voiceSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    navigator.mediaDevices?.getUserMedia !== undefined &&
    (window.AudioContext !== undefined ||
      (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext !==
        undefined)
  );
}

function noteKey(note: string): string {
  switch (note) {
    case NOTE_MIC:
      return 'mic';
    case NOTE_UNAVAILABLE:
      return 'unavailable';
    case NOTE_DROPPED:
      return 'dropped';
    case NOTE_NOT_SENT:
      return 'not-sent';
    default:
      return 'other';
  }
}

function seqOf(message: Message): bigint {
  return BigInt(message.seq);
}

/** Where one transcription session's events go once it is no longer the current one. */
type DrainHandlers = {
  onPartial: (text: string, order: number) => void;
  onFinal: (text: string, order: number) => void;
  onClosed: () => void;
};

export class VoiceEngine {
  state: VoiceState = {
    on: false,
    phase: 'starting',
    partial: '',
    note: null,
    unsentVersion: 0,
  };

  readonly #client: ApiClient;
  readonly #planId: string;
  readonly #onChange: (state: VoiceState) => void;

  #audio: AudioContext | null = null;
  #capture: Capture | null = null;
  #stt: Transcription | null = null;
  // Sends the current session's later events to a drain instead of the engine.
  #handOff: ((handlers: DrainHandlers) => void) | null = null;
  // Sessions handed to a drain and not yet terminated, so closing the page can end them.
  readonly #draining = new Set<Transcription>();
  #speaker: Speaker | null = null;
  // Incremented by every start and end, so a stale async step can tell it is stale.
  #generation = 0;
  // One per start, so a post from an earlier voice session can be told apart.
  #session = 0;

  #target: VoiceTarget | null = null;
  #nullTimer: ReturnType<typeof setTimeout> | null = null;
  // The highest `seq` that had arrived when voice started or this target became current.
  // Null until the target reports its first page is in.
  #spokenAfter: bigint | null = null;
  #queue: string[] = [];
  #pumping = false;
  #speaking = false;

  #finals: string[] = [];
  #lastPartial = '';
  #heardOrder = -1;
  #committedOrder = -1;
  #settleTimer: ReturnType<typeof setTimeout> | null = null;
  #stallTimer: ReturnType<typeof setTimeout> | null = null;

  #postChain: Promise<void> = Promise.resolve();
  #postsBlocked = false;
  readonly #unsent = new Map<string, string>();

  #thinking = false;
  #busySeen = false;
  #thinkingTimer: ReturnType<typeof setTimeout> | null = null;

  #heldFrames: Int16Array[] = [];
  readonly #levelListeners = new Set<(level: number) => void>();
  #loudSince = 0;
  #levelAt = 0;

  constructor(client: ApiClient, planId: string, onChange: (state: VoiceState) => void) {
    this.#client = client;
    this.#planId = planId;
    this.#onChange = onChange;
  }

  readonly #onPageHide = (): void => {
    for (const session of this.#draining) {
      session.terminate();
    }
    this.#draining.clear();
    this.#endNow(null);
  };

  /**
   * Closing the page ends voice now, so the usage reports go out before it is gone.
   * Paired with `detach` by the hook's effect, so a repeated setup keeps the listener.
   */
  attach(): void {
    window.addEventListener('pagehide', this.#onPageHide);
  }

  detach(): void {
    window.removeEventListener('pagehide', this.#onPageHide);
  }

  // ------------------------------------------------------------------ public

  /** Must run inside the click: the audio unlock and the permission prompt need it. */
  start(): void {
    if (this.state.on || !voiceSupported()) {
      return;
    }
    const generation = (this.#generation += 1);
    this.#session += 1;
    voiceLog('voice start', { session: this.#session });
    const AudioContextClass: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const audio = (this.#audio ??= new AudioContextClass());
    void audio.resume();
    // Safari plays later audio only after one sound has been started inside a click.
    const silent = audio.createBuffer(1, 1, audio.sampleRate);
    const unlock = audio.createBufferSource();
    unlock.buffer = silent;
    unlock.connect(audio.destination);
    unlock.start();

    this.#resetTurn();
    this.#committedOrder = -1;
    this.#queue = [];
    this.#postsBlocked = false;
    this.#spokenAfter = null;
    this.#heldFrames = [];
    this.#set({ on: true, phase: 'starting', partial: '', note: null });
    this.#considerMessages();

    // Both are started now, in parallel; the microphone's first call is made inside the click.
    const capturing = startCapture(
      audio,
      (frame) => this.#onFrame(frame),
      (rms) => this.#onLevel(rms),
    );
    const tokens = this.#client.voiceToken(this.#planId);
    void Promise.allSettled([capturing, tokens]).then(([cap, tok]) => {
      if (generation !== this.#generation) {
        if (cap.status === 'fulfilled') {
          cap.value.stop();
        }
        return;
      }
      if (cap.status === 'rejected') {
        this.#fail(NOTE_MIC);
        return;
      }
      if (tok.status === 'rejected') {
        cap.value.stop();
        this.#fail(this.#tokenNote(tok.reason));
        return;
      }
      this.#capture = cap.value;
      this.#speaker = createSpeaker({
        initial: tok.value.cartesia,
        getToken: async () => (await this.#client.voiceToken(this.#planId)).cartesia,
        audioContext: audio,
        onDropped: () => this.#fail(NOTE_DROPPED),
        onUsage: (characters, modelId) => {
          this.#client
            .reportVoiceUsage(this.#planId, {
              kind: 'speech',
              model: modelId,
              characters,
            })
            .catch((error: unknown) =>
              voiceLog('voice usage report failed', { error: String(error) }),
            );
        },
      });
      this.#openStt(tok.value);
      this.#set({ phase: 'listening' });
      this.#pump();
    });
  }

  /** The person ended voice: what was said but not sent goes to that conversation's box. */
  end(): void {
    this.#endNow(null);
  }

  /** Speech not yet sent for that conversation, cleared on read. */
  takeUnsent(threadId: string): string {
    const text = this.#unsent.get(threadId) ?? '';
    this.#unsent.delete(threadId);
    return text;
  }

  setTarget(target: VoiceTarget | null): void {
    if (target === null) {
      // A route change unmounts one conversation and mounts the next in quick succession;
      // only a null that is still alone a moment later is the conversation going away.
      if (this.#nullTimer === null) {
        this.#nullTimer = setTimeout(() => {
          this.#nullTimer = null;
          this.#targetGone();
        }, 0);
      }
      return;
    }
    if (this.#nullTimer !== null) {
      clearTimeout(this.#nullTimer);
      this.#nullTimer = null;
    }
    const previous = this.#target;
    if (!this.state.on) {
      this.#target = target;
      return;
    }
    if (previous !== null && previous.threadId !== target.threadId) {
      this.#switchTo(target);
      return;
    }
    this.#target = target;
    this.#watchBusy(target);
    this.#considerMessages();
  }

  /**
   * The microphone level (0..1), several times a second. Kept out of `state` so the meter
   * repaints alone instead of the whole plan screen.
   */
  subscribeLevel(listener: (level: number) => void): () => void {
    this.#levelListeners.add(listener);
    return () => this.#levelListeners.delete(listener);
  }

  #emitLevel(level: number): void {
    this.#levelListeners.forEach((listener) => listener(level));
  }

  /** The screen is going away. */
  dispose(): void {
    if (this.#nullTimer !== null) {
      clearTimeout(this.#nullTimer);
      this.#nullTimer = null;
    }
    this.#flushHeard();
    this.#endNow(null);
    this.#target = null;
    const audio = this.#audio;
    this.#audio = null;
    void audio?.close().catch(() => undefined);
  }

  // ------------------------------------------------------------------ state

  #set(patch: Partial<VoiceState>): void {
    this.state = { ...this.state, ...patch };
    this.#onChange(this.state);
  }

  #refreshPhase(): void {
    if (!this.state.on) {
      return;
    }
    if (this.state.phase === 'starting') {
      return;
    }
    const phase: VoicePhase = this.#speaking
      ? 'speaking'
      : this.#thinking
        ? 'thinking'
        : 'listening';
    if (phase !== this.state.phase) {
      this.#set({ phase });
    }
  }

  #tokenNote(reason: unknown): string {
    return reason instanceof ApiFailure && reason.code === 'voice_unavailable'
      ? NOTE_UNAVAILABLE
      : NOTE_DROPPED;
  }

  #fail(note: string): void {
    voiceLog('voice fail', { note: noteKey(note) });
    this.#endNow(note);
  }

  // ------------------------------------------------------------------ ending

  #endNow(note: string | null): void {
    if (!this.state.on) {
      if (note !== null) {
        this.#set({ note });
      }
      return;
    }
    voiceLog('voice end', { note: note === null ? null : noteKey(note) });
    this.#generation += 1;
    this.#clearTurnTimers();
    this.#clearThinking();
    this.#stopSpeech();
    this.#speaker?.close();
    this.#speaker = null;
    this.#stt?.terminate();
    this.#stt = null;
    this.#handOff = null;
    this.#capture?.stop();
    this.#capture = null;
    this.#heldFrames = [];
    const text = this.#takeHeard();
    if (text !== '' && this.#target !== null) {
      this.#keepUnsent(this.#target.threadId, text);
    }
    this.#emitLevel(0);
    this.#set({ on: false, phase: 'starting', partial: '', note });
  }

  #targetGone(): void {
    this.#flushHeard();
    this.#target = null;
    this.#endNow(null);
  }

  // ------------------------------------------------------------------ switching

  #switchTo(target: VoiceTarget): void {
    voiceLog('switch', {
      from: this.#target?.threadId,
      to: target.threadId,
      wordsFlushed: wordCount(this.#finals.join(' ')),
      wordsHeld: wordCount(this.#lastPartial),
      starting: this.state.phase === 'starting',
      heldFramesDiscarded: this.state.phase === 'starting' ? this.#heldFrames.length : 0,
    });
    if (this.state.phase === 'starting') {
      // Still opening the first session: it simply starts on the new conversation. Audio held
      // until then was meant for the previous one, and none of it was shown or sent.
      this.#heldFrames = [];
      this.#target = target;
      this.#spokenAfter = null;
      this.#considerMessages();
      return;
    }
    const generation = (this.#generation += 1);
    this.#stopSpeech();
    this.#clearTurnTimers();
    this.#clearThinking();
    this.#handOver();
    this.#committedOrder = -1;
    this.#heardOrder = -1;
    this.#target = target;
    this.#spokenAfter = null;
    this.#set({ phase: 'listening', partial: '' });
    this.#considerMessages();
    this.#client.voiceToken(this.#planId).then(
      (tokens) => {
        if (generation !== this.#generation) {
          return;
        }
        this.#openStt(tokens);
      },
      (reason: unknown) => {
        if (generation === this.#generation) {
          this.#fail(this.#tokenNote(reason));
        }
      },
    );
  }

  /**
   * Finished turns go to the current target now. The session itself is not closed at once:
   * it is told to end the turn, gets no more audio, and has `DRAIN_MS` to finish the text
   * it already has, which goes to the same conversation.
   */
  #handOver(): void {
    const previous = this.#target;
    const finished = this.#finals.join(' ').trim();
    const held = this.#lastPartial;
    const heldOrder = this.#heardOrder;
    // Every turn up to here is posted; the held partial's turn is left to the drain.
    const postedThrough = Math.max(
      this.#committedOrder,
      held === '' ? this.#heardOrder : this.#heardOrder - 1,
    );
    this.#clearTurnTimers();
    this.#takeHeard();
    if (finished !== '' && previous !== null) {
      voiceLog('commit (flush)', {
        words: wordCount(finished),
        threadId: previous.threadId,
      });
      this.#send(previous, finished);
    }
    const session = this.#stt;
    const handOff = this.#handOff;
    this.#stt = null;
    this.#handOff = null;
    if (session === null || handOff === null || previous === null) {
      session?.terminate();
      if (held !== '' && previous !== null) {
        this.#send(previous, held);
      }
      return;
    }
    this.#drain(session, handOff, previous, postedThrough, held, heldOrder);
  }

  #drain(
    session: Transcription,
    handOff: (handlers: DrainHandlers) => void,
    target: VoiceTarget,
    postedThrough: number,
    held: string,
    heldOrder: number,
  ): void {
    // Its own state: nothing here touches the new session's turn.
    let posted = postedThrough;
    let done = false;
    this.#draining.add(session);
    let timer: ReturnType<typeof setTimeout> | null = null;
    const partials = new Map<number, string>();
    if (held !== '') {
      partials.set(heldOrder, held);
    }
    const post = (text: string, order: number, kind: 'final' | 'partial'): void => {
      voiceLog('drain post', {
        order,
        kind,
        words: wordCount(text),
        threadId: target.threadId,
      });
      this.#send(target, text);
    };
    // Partials of turns before `order` that never got a final, oldest first.
    const postPartialsBefore = (order: number): void => {
      const earlier = [...partials.keys()]
        .filter((key) => key < order)
        .sort((a, b) => a - b);
      for (const key of earlier) {
        const text = partials.get(key) as string;
        partials.delete(key);
        if (key > posted) {
          post(text, key, 'partial');
          posted = key;
        }
      }
    };
    const finish = (reason: 'final' | 'closed' | 'timeout'): void => {
      if (done) {
        return;
      }
      done = true;
      if (timer !== null) {
        clearTimeout(timer);
      }
      postPartialsBefore(Number.POSITIVE_INFINITY);
      session.terminate();
      this.#draining.delete(session);
      voiceLog('drain end', { reason, threadId: target.threadId });
    };
    handOff({
      onPartial: (raw, order) => {
        const text = raw.trim();
        if (done || order <= posted || text === '') {
          return;
        }
        partials.set(order, text);
      },
      onFinal: (raw, order) => {
        const text = raw.trim();
        if (done || order <= posted || text === '') {
          voiceLog('drain final ignored', { order, posted });
          return;
        }
        postPartialsBefore(order);
        partials.delete(order);
        post(text, order, 'final');
        posted = order;
        if (partials.size === 0) {
          finish('final');
        }
      },
      onClosed: () => finish('closed'),
    });
    session.forceEndpoint();
    timer = setTimeout(() => finish('timeout'), DRAIN_MS);
    voiceLog('drain start', {
      threadId: target.threadId,
      heldWords: wordCount(held),
      posted,
    });
  }

  // ------------------------------------------------------------------ listening

  #openStt(tokens: VoiceTokens): void {
    const generation = this.#generation;
    const live = (): boolean => generation === this.#generation;
    let drain: DrainHandlers | null = null;
    const session = openTranscription({
      token: tokens.assemblyAi.token,
      minTurnSilenceMs: tokens.turn.minTurnSilenceMs,
      maxTurnSilenceMs: tokens.turn.maxTurnSilenceMs,
      onUsage: (seconds) => {
        this.#client
          .reportVoiceUsage(this.#planId, {
            kind: 'transcription',
            model: SPEECH_MODEL,
            seconds,
          })
          .catch((error: unknown) =>
            voiceLog('voice usage report failed', { error: String(error) }),
          );
      },
      onPartial: (text, order) => {
        if (drain !== null) {
          drain.onPartial(text, order);
        } else if (live()) {
          this.#onPartial(text, order);
        }
      },
      onFinal: (text, order) => {
        if (drain !== null) {
          drain.onFinal(text, order);
        } else if (live()) {
          this.#onFinal(text, order);
        }
      },
      onSpeechStarted: () => {
        if (drain === null && live() && this.#speaking) {
          this.#interrupt('speech-started');
        }
      },
      onClosed: () => {
        if (drain !== null) {
          drain.onClosed();
        } else if (live()) {
          this.#fail(NOTE_DROPPED);
        }
      },
    });
    this.#stt = session;
    this.#handOff = (handlers) => {
      drain = handlers;
    };
    for (const frame of this.#heldFrames) {
      session.send(frame);
    }
    this.#heldFrames = [];
  }

  #onFrame(frame: Int16Array): void {
    if (this.#stt !== null) {
      this.#stt.send(frame);
      return;
    }
    if (this.state.on) {
      this.#heldFrames.push(frame);
      if (this.#heldFrames.length > MAX_HELD_FRAMES) {
        this.#heldFrames.shift();
      }
    }
  }

  #onLevel(rms: number): void {
    if (!this.state.on) {
      return;
    }
    const now = performance.now();
    if (this.#speaking && rms > BARGE_RMS) {
      if (this.#loudSince === 0) {
        this.#loudSince = now;
      } else if (now - this.#loudSince > BARGE_MS) {
        this.#interrupt('level');
      }
    } else {
      this.#loudSince = 0;
    }
    if (now - this.#levelAt >= LEVEL_EVERY_MS) {
      this.#levelAt = now;
      this.#emitLevel(Math.min(1, rms * 4));
    }
  }

  #onPartial(raw: string, order: number): void {
    const text = raw.trim();
    if (order <= this.#committedOrder || text === '') {
      voiceLog('partial ignored', {
        order,
        reason: text === '' ? 'empty' : 'already-committed',
        committedOrder: this.#committedOrder,
      });
      return;
    }
    voiceLog('partial accepted', { order, words: wordCount(text) });
    if (this.#speaking && /\S+\s+\S+/u.test(text)) {
      this.#interrupt('partial');
    }
    this.#heardOrder = Math.max(this.#heardOrder, order);
    // A new partial means the speaker is still going: the settle wait starts over.
    this.#clearTimer('settle');
    if (text !== this.#lastPartial) {
      this.#lastPartial = text;
      this.#clearTimer('stall');
      this.#stallTimer = setTimeout(() => {
        voiceLog('stall timer fired');
        this.#commit();
      }, STALL_MS);
    }
    this.#set({ partial: [...this.#finals, text].join(' ') });
  }

  #onFinal(raw: string, order: number): void {
    const text = raw.trim();
    if (order <= this.#committedOrder || text === '') {
      voiceLog('final ignored', {
        order,
        reason: text === '' ? 'empty' : 'already-committed',
        committedOrder: this.#committedOrder,
      });
      return;
    }
    voiceLog('final accepted', { order, words: wordCount(text) });
    if (this.#speaking) {
      this.#interrupt('final');
    }
    this.#heardOrder = Math.max(this.#heardOrder, order);
    this.#finals.push(text);
    this.#lastPartial = '';
    this.#clearTimer('stall');
    this.#clearTimer('settle');
    this.#settleTimer = setTimeout(() => {
      voiceLog('settle timer fired');
      this.#commit();
    }, SETTLE_MS);
    this.#set({ partial: this.#finals.join(' ') });
  }

  #commit(): void {
    this.#clearTurnTimers();
    const text = this.#takeHeard();
    this.#committedOrder = Math.max(this.#committedOrder, this.#heardOrder);
    if (text === '' || !this.state.on || this.#target === null) {
      voiceLog('commit dropped', {
        reason: text === '' ? 'empty' : !this.state.on ? 'voice-off' : 'no-target',
      });
      return;
    }
    voiceLog('commit', { words: wordCount(text), threadId: this.#target.threadId });
    this.#send(this.#target, text);
  }

  #takeHeard(): string {
    const text = [...this.#finals, this.#lastPartial].join(' ').trim();
    this.#resetTurn();
    if (this.state.partial !== '') {
      this.#set({ partial: '' });
    }
    return text;
  }

  #resetTurn(): void {
    this.#finals = [];
    this.#lastPartial = '';
  }

  /** What was said so far goes to `target` now, without asking the vendor to end the turn. */
  #flushHeard(): void {
    this.#clearTurnTimers();
    const text = this.#takeHeard();
    this.#committedOrder = Math.max(this.#committedOrder, this.#heardOrder);
    if (text !== '' && this.#target !== null) {
      voiceLog('commit (flush)', {
        words: wordCount(text),
        threadId: this.#target.threadId,
      });
      this.#send(this.#target, text);
    }
  }

  #clearTimer(which: 'settle' | 'stall'): void {
    if (which === 'settle' && this.#settleTimer !== null) {
      clearTimeout(this.#settleTimer);
      this.#settleTimer = null;
    }
    if (which === 'stall' && this.#stallTimer !== null) {
      clearTimeout(this.#stallTimer);
      this.#stallTimer = null;
    }
  }

  #clearTurnTimers(): void {
    this.#clearTimer('settle');
    this.#clearTimer('stall');
  }

  // ------------------------------------------------------------------ posting

  /** Posts are serialized; a failed post never stops the links after it. */
  #send(target: VoiceTarget, text: string): void {
    const session = this.#session;
    const run = async (): Promise<void> => {
      if (this.#postsBlocked && session === this.#session) {
        voiceLog('post skipped', { reason: 'posts-blocked', threadId: target.threadId });
        this.#keepUnsent(target.threadId, text);
        return;
      }
      voiceLog('post sent', { words: wordCount(text), threadId: target.threadId });
      try {
        await target.post(text);
      } catch (error) {
        voiceLog('post failed', {
          threadId: target.threadId,
          code: error instanceof ApiFailure ? error.code : undefined,
        });
        this.#keepUnsent(target.threadId, text);
        // A post from an earlier voice session only keeps its text; it must not end this one.
        if (session === this.#session) {
          this.#postsBlocked = true;
          this.#fail(NOTE_NOT_SENT);
        }
        return;
      }
      voiceLog('post succeeded', { threadId: target.threadId });
      if (
        session === this.#session &&
        this.state.on &&
        this.#target?.threadId === target.threadId
      ) {
        this.#startThinking();
      }
    };
    this.#postChain = this.#postChain.then(run, run);
  }

  #keepUnsent(threadId: string, text: string): void {
    voiceLog('kept as unsent', { words: wordCount(text), threadId });
    const existing = this.#unsent.get(threadId);
    this.#unsent.set(threadId, existing === undefined ? text : `${existing} ${text}`);
    this.#set({ unsentVersion: this.state.unsentVersion + 1 });
  }

  // ------------------------------------------------------------------ thinking

  #startThinking(): void {
    this.#thinking = true;
    this.#busySeen = this.#target?.facilitatorBusy === true;
    this.#clearThinkingTimer();
    this.#thinkingTimer = setTimeout(() => this.#stopThinking(), THINKING_TIMEOUT_MS);
    this.#refreshPhase();
  }

  #stopThinking(): void {
    this.#thinking = false;
    this.#clearThinkingTimer();
    this.#refreshPhase();
  }

  #clearThinking(): void {
    this.#thinking = false;
    this.#clearThinkingTimer();
  }

  #clearThinkingTimer(): void {
    if (this.#thinkingTimer !== null) {
      clearTimeout(this.#thinkingTimer);
      this.#thinkingTimer = null;
    }
  }

  // Not busy right after the post does not end thinking: the worker may not have picked the
  // plan up yet. Busy having been seen, and then not busy, does.
  #watchBusy(target: VoiceTarget): void {
    if (!this.#thinking) {
      return;
    }
    if (target.facilitatorBusy) {
      this.#busySeen = true;
    } else if (this.#busySeen) {
      this.#stopThinking();
    }
  }

  // ------------------------------------------------------------------ speaking

  /**
   * Queues the text of new messages to speak: Mor's, and a person's whose roster entry is not
   * the reader's own. Only `seq` above the watermark; system lines are never spoken. The
   * sender's name is added here, when the message is queued, if the target says to.
   */
  #considerMessages(): void {
    const target = this.#target;
    if (!this.state.on || target === null || !target.loaded) {
      return;
    }
    if (this.#spokenAfter === null) {
      this.#spokenAfter = target.messages.reduce(
        (highest, message) => (seqOf(message) > highest ? seqOf(message) : highest),
        -1n,
      );
      return;
    }
    const watermark = this.#spokenAfter;
    const fresh = target.messages
      .filter((message) => seqOf(message) > watermark)
      .sort((a, b) => (seqOf(a) < seqOf(b) ? -1 : seqOf(a) > seqOf(b) ? 1 : 0));
    let queued = false;
    let facilitatorQueued = false;
    for (const message of fresh) {
      this.#spokenAfter = seqOf(message);
      if (message.body.trim() === '') {
        continue;
      }
      let name: string;
      if (message.type === 'facilitator') {
        name = FACILITATOR_NAME;
        facilitatorQueued = true;
      } else if (message.type === 'human') {
        const author = target.people.find((person) => person.id === message.authorId);
        if (author?.self === true) {
          continue;
        }
        name = author?.name ?? 'Someone';
      } else {
        continue;
      }
      const body = message.body.replace(
        FILE_MARKER,
        (_whole, _id, words: string) => words,
      );
      this.#queue.push(target.named ? `${name} says: ${body}` : body);
      queued = true;
    }
    if (facilitatorQueued) {
      this.#stopThinking();
    }
    if (queued) {
      this.#pump();
    }
  }

  #pump(): void {
    if (this.#pumping || this.#speaker === null || this.#queue.length === 0) {
      return;
    }
    this.#pumping = true;
    const generation = this.#generation;
    void (async () => {
      try {
        while (generation === this.#generation && this.#speaker !== null) {
          const next = this.#queue.shift();
          if (next === undefined) {
            break;
          }
          this.#speaking = true;
          this.#refreshPhase();
          // A message the vendor rejects is skipped; its text is on screen. A connection that
          // fails ends voice through the speaker's `onDropped`.
          await this.#speaker.speak(next);
        }
      } finally {
        this.#pumping = false;
        this.#speaking = false;
        this.#refreshPhase();
        // Something queued while this one was winding down (a switch) still gets spoken.
        this.#pump();
      }
    })();
  }

  /** Stops what is being said and forgets the rest of the queue. The text stays on screen. */
  #interrupt(trigger: 'level' | 'partial' | 'final' | 'speech-started'): void {
    voiceLog('barge-in', { trigger });
    this.#stopSpeech();
    this.#refreshPhase();
  }

  #stopSpeech(): void {
    this.#queue = [];
    this.#speaking = false;
    this.#loudSince = 0;
    this.#speaker?.stopAll();
  }
}
